import type { GeoJsonData } from '../../../../../app-kit/components/PcaMap/PcaMap';
import type { MapViewType } from '../../../../../app-kit/components/PcaMap/types';
import { PcaMapContext } from '../../../../contexts/PcaMapContext/PcaMapContext';
import { ContentSwitcher, ContentSwitcherButton } from '@property.com.au/ui-kit';
import { useChangeMapType } from '../../helpers/useChangeMapType';
import { useIsStreetViewSupported } from '../../helpers/useIsStreetViewSupported';
import type { FunctionComponent } from 'react';
import { useContext, useState } from 'react';
import { usePropertyPageQuery } from '../../../../graphql/usePropertyPageQuery';

interface MapTypeSwitcherProp {
  propertyBoundary?: GeoJsonData | null;
  buildingBoundaries?: GeoJsonData | null;
}

export const MapTypeSwitcher: FunctionComponent<MapTypeSwitcherProp> = ({
  propertyBoundary,
  buildingBoundaries,
}) => {
  const [mapType, setMapType] = useState<MapViewType>('roadmap');
  const { map } = useContext(PcaMapContext);
  const isStreetViewSupported = useIsStreetViewSupported();

  const callback = useChangeMapType({
    propertyBoundary,
    buildingBoundaries,
  });

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as MapViewType;
    setMapType(value);
    callback(value);
  };

  return (
    <ContentSwitcher
      id="map-type-switcher"
      label="Map type switcher"
      sizeVariant="small"
      disabled={!map}
    >
      <ContentSwitcherButton
        id="map-type-roadmap"
        value="roadmap"
        label="Default"
        onChange={onChange}
        checked={mapType === 'roadmap'}
      />
      <ContentSwitcherButton
        id="map-type-satellite"
        value="satellite"
        label="Satellite"
        onChange={onChange}
        checked={mapType === 'satellite'}
      />
      {isStreetViewSupported && (
        <ContentSwitcherButton
          id="map-type-street-view"
          value="street_view"
          label="Street"
          onChange={onChange}
          checked={mapType === 'street_view'}
        />
      )}
    </ContentSwitcher>
  );
};

export const MapTypeSwitcherWithData: FunctionComponent = () => {
  const data = usePropertyPageQuery()?.data.propertyPageBySlugV3;

  return (
    <MapTypeSwitcher
      propertyBoundary={data?.propertyMap?.propertyBoundary}
      buildingBoundaries={data?.propertyMap?.buildingBoundaries}
    />
  );
};
